import type {
  BrandSpecialQualifyingStatus,
  SpecialQualifyingStatus,
} from '@iheartjane/data-access';
import { getSpecialQualifyingMetrics } from '@iheartjane/menu';
import { formatCurrency, pluralize } from '@iheartjane/utils';
import sumBy from 'lodash-es/sumBy';

/* eslint-disable-next-line no-restricted-imports */
import type { BrandSpecial as tBrandSpecial } from '@jane/shared/models';
import type { BrandSpecial, StoreSpecial } from '@jane/shared/types';

interface GetBrandSpecialStatusText {
  currentSpecial?: BrandSpecial | tBrandSpecial;
  qualifyingStatus?: BrandSpecialQualifyingStatus | null;
}

export const getBrandSpecialStatusText = ({
  qualifyingStatus,
  currentSpecial,
}: GetBrandSpecialStatusText) => {
  const isApplied = qualifyingStatus?.qualifying_status === 'applied';
  const isQualified = qualifyingStatus?.qualifying_status === 'qualified';
  const isUnlocked = isApplied || isQualified;

  const remainingQualifiers =
    (currentSpecial?.rules?.settings?.threshold_number_of_items_in_cart || 0) -
    sumBy(qualifyingStatus?.qualified_products, 'count');
  const remainingDiscountables =
    (currentSpecial?.rules?.settings?.max_number_of_discounted_products || 0) -
    sumBy(qualifyingStatus?.discounted_products, 'count');

  const reachedMaxDiscountables = remainingDiscountables === 0;
  const showQualifierText = remainingQualifiers > 0;
  const hasCashBack =
    qualifyingStatus?.discount_total_with_fee &&
    qualifyingStatus?.discount_total_with_fee > 0;
  const formattedTotalAmount = formatCurrency(
    qualifyingStatus?.discount_total_with_fee,
    true
  );

  const productText = showQualifierText
    ? `Add ${pluralize({
        noun: 'product',
        number: remainingQualifiers,
        returnValue: 'both',
      })} to unlock this reward`
    : remainingDiscountables > 0
      ? `Add up to ${remainingDiscountables} more ${pluralize({
          noun: 'product',
          number: remainingDiscountables,
          returnValue: 'word',
        })}`
      : undefined;

  const totalCashBack = `${formattedTotalAmount} total cash back!`;

  const rewardStatusText = !isUnlocked
    ? undefined
    : !hasCashBack
      ? 'Reward unlocked!'
      : !reachedMaxDiscountables
        ? `Reward unlocked with ${totalCashBack}`
        : `You maxed out your reward with ${totalCashBack}`;

  const calloutText = [rewardStatusText, productText].join(' ').trim();

  return {
    calloutText,
    remainingQualifiers,
    reachedMaxDiscountables,
  };
};

interface GetStoreSpecialStatusText {
  currentSpecial: StoreSpecial;
  qualifyingStatus?: SpecialQualifyingStatus | null;
}

export const getStoreSpecialStatusText = ({
  qualifyingStatus,
  currentSpecial,
}: GetStoreSpecialStatusText) => {
  const {
    remainingQualifiers,
    remainingDiscountables,
    reachedMaxDiscountables,
  } = getSpecialQualifyingMetrics({ currentSpecial, qualifyingStatus });

  const isApplied = qualifyingStatus?.qualifying_status === 'applied';
  const isQualified = qualifyingStatus?.qualifying_status === 'qualified';
  const isUnlocked = isApplied || isQualified;
  const isSpendingThresholdType =
    currentSpecial.special_type === 'spending_threshold';
  const showQualifierText = remainingQualifiers > 0;

  const formattedTotalAmount = formatCurrency(
    qualifyingStatus?.discount_total_amount,
    true
  );

  const qualifyText = isSpendingThresholdType
    ? `Spend ${formatCurrency(remainingQualifiers, true)}`
    : `Add ${pluralize({
        noun: 'product',
        number: remainingQualifiers,
        returnValue: 'both',
      })}`;

  const requiredQualifierCount =
    currentSpecial.special_type === 'bundle'
      ? currentSpecial.conditions?.bundle?.independent
          ?.threshold_number_of_items_in_cart
      : undefined;

  const productText = showQualifierText
    ? `${qualifyText} to unlock this reward`
    : remainingDiscountables > 0
      ? `Add up to ${remainingDiscountables} more ${pluralize({
          noun: 'product',
          number: remainingDiscountables,
          returnValue: 'word',
        })}`
      : !isUnlocked
        ? requiredQualifierCount === 1
          ? 'Try a different item to unlock this reward'
          : 'Try different items to unlock this reward'
        : undefined;

  const totalDealText = `${formattedTotalAmount} saved with this deal!`;

  const rewardStatusText = !isUnlocked
    ? undefined
    : !(
          qualifyingStatus?.discount_total_amount &&
          qualifyingStatus?.discount_total_amount > 0
        )
      ? 'Reward unlocked!'
      : !reachedMaxDiscountables
        ? `Reward unlocked with ${totalDealText}`
        : `You maxed out your reward with ${totalDealText}`;

  const calloutText = [rewardStatusText, productText].join(' ').trim();

  return {
    calloutText,
    remainingQualifiers,
    reachedMaxDiscountables,
  };
};
